import { inject, RESPONSE_INIT } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { SeoService } from '../../../shared/services/seo.service';
import {
  applyBlogSeoSafely,
  buildBlogArticleNotFoundSeo,
  buildBlogArticleSeo,
  buildBlogLoadErrorSeo,
  setBlogLoadFailureStatus,
  setBlogResponseStatus,
} from '../utils/blog-seo.util';
import { BlogService } from './blog.service';
import {
  BLOG_LOAD_ERROR,
  isBlogLoadError,
  type BlogArticleView,
  type BlogLoadError,
} from './blog-view.model';

/** Lo que la página lee de `route.data['article']`. */
export type BlogArticleResolved = BlogArticleView | null | BlogLoadError;

/**
 * Resuelve la ficha de `/blog/:slug` y aplica su SEO ANTES de activar la
 * página (B61, mismo motivo que `seoResolver`): así el HTML del SSR ya lleva
 * OG, canonical y el JSON-LD `BlogPosting` en la primera respuesta.
 *
 * - **Slug retirado (B122):** la API responde 301 y `HttpClient` lo sigue, así
 *   que llega la entrada con su slug canónico. Si no coincide con el de la
 *   URL, se redirige a la canónica: en SSR con un 301 real (`RESPONSE_INIT`,
 *   que `@angular/ssr` convierte en `Location`), en cliente con `replaceUrl`.
 *   No se aplica SEO: esta URL no se pinta.
 * - **Slug inexistente (B62, B132 b):** `null` → la página pinta «no
 *   encontrado», `noindex,nofollow` y, en SSR, un 404 real.
 * - **API caída (B123, B132 a):** `BLOG_LOAD_ERROR` → estado de error,
 *   `noindex,nofollow` y, en SSR, 503 + `Retry-After`. Un error interno (no
 *   HTTP ni timeout, p. ej. el mapper) pinta lo mismo pero responde 500 sin
 *   `Retry-After` (Astra W5, B2).
 *
 * Solo la llamada a la API decide el 503 (Astra W5, A1): el `catchError` va
 * pegado a `getBySlug` y el SEO se aplica después con `applyBlogSeoSafely`, así
 * que un fallo de SEO se registra y no cambia ni la vista ni el estado HTTP.
 */
export const blogArticleResolver: ResolveFn<BlogArticleResolved> = (route) => {
  const blog = inject(BlogService);
  const seo = inject(SeoService);
  const router = inject(Router);
  const responseInit = inject(RESPONSE_INIT, { optional: true });
  const slug = route.paramMap.get('slug') ?? '';

  return blog.getBySlug(slug).pipe(
    // Solo los errores del servicio llegan aquí; el SEO va después (A1). El
    // estado HTTP depende de quién falló (B2): fuente → 503 + Retry-After;
    // error interno → 500, registrado.
    catchError((err: unknown) => {
      setBlogLoadFailureStatus(responseInit, err);
      return of(BLOG_LOAD_ERROR);
    }),
    map((result) => {
      if (isBlogLoadError(result)) {
        applyBlogSeoSafely(seo, buildBlogLoadErrorSeo);
        return result;
      }
      if (result && result.post.slug !== slug) {
        setBlogResponseStatus(responseInit, 301);
        return new RedirectCommand(router.createUrlTree(['/blog', result.post.slug]), {
          replaceUrl: true,
        });
      }
      if (!result) setBlogResponseStatus(responseInit, 404);
      applyBlogSeoSafely(seo, () =>
        result ? buildBlogArticleSeo(result.post) : buildBlogArticleNotFoundSeo(),
      );
      return result;
    }),
  );
};
